import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { theme } from '../styles/theme';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
}

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: ${theme.spacing[4]};
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${theme.colors.border};
  border-top: 3px solid ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.base};
`;

const AccessDenied = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
  gap: ${theme.spacing[4]};
  
  h2 {
    color: ${theme.colors.textPrimary};
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semibold};
  }
  
  p {
    color: ${theme.colors.textMuted};
    font-size: ${theme.typography.fontSize.base};
    max-width: 400px;
  }
`;

const BackButton = styled.button`
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: background-color ${theme.transitions.base};
  
  &:hover {
    background: ${theme.colors.primary[600]};
  }
`;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  adminOnly = false 
}) => {
  const { state } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (state.isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Verifying authentication...</LoadingText>
      </LoadingContainer>
    );
  }

  // Redirect to login if not authenticated
  if (!state.isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check admin requirement
  if (adminOnly && !state.user?.is_admin) {
    return (
      <AccessDenied>
        <h2>Access Denied</h2>
        <p>
          You don't have the required permissions to access this page. 
          Please contact an administrator if you believe this is an error.
        </p>
        <BackButton onClick={() => window.history.back()}>
          Go Back
        </BackButton>
      </AccessDenied>
    );
  }

  // Check if user account is active
  if (!state.user?.is_active) {
    return (
      <AccessDenied>
        <h2>Account Inactive</h2>
        <p>
          Your account has been deactivated. Please contact an administrator 
          for assistance.
        </p>
        <BackButton onClick={() => window.location.href = '/login'}>
          Return to Login
        </BackButton>
      </AccessDenied>
    );
  }

  // Render protected content
  return <>{children}</>;
};

export default ProtectedRoute;